/**
 * PRICING PAGE
 * 
 * Public-facing page showing subscription tiers and pricing
 * Includes monthly/yearly toggle and Stripe checkout integration
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Sparkles, Crown, Star, Gem, Heart, Loader2, CreditCard } from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { auth } from '../firebase/config';
import { PageHeader } from '../components/common';

// ============================================
// PRICE IDS
// ============================================
// Swap these on launch day: uncomment LIVE, comment out TEST

// TEST MODE PRICE IDS (current)
// const PRICE_IDS = {
//   casual: {
//     monthly: 'price_1Sv9geECD1n5qAAsprZgQkP7',
//     yearly: 'price_1Sv9h7ECD1n5qAAsBGN25WyZ',
//   },
//   enthusiast: {
//     monthly: 'price_1Sv9ihECD1n5qAAsMMwQwXRs',
//     yearly: 'price_1Sv9jIECD1n5qAAsDzeGIZMI',
//   },
//   lover: {
//     monthly: 'price_1Sv9k1ECD1n5qAAszNf37hh2',
//     yearly: 'price_1Sv9kPECD1n5qAAsZcKF9kF4',
//   },
//   pro: {
//     monthly: 'price_1Sv9kxECD1n5qAAswmVJLCzX',
//     yearly: 'price_1Sv9lNECD1n5qAAsLuf5LoCM',
//   },
// };

// LIVE MODE PRICE IDS (uncomment on launch day)
const PRICE_IDS = {
  casual: {
    monthly: 'price_1Sv9CDECD1n5qAAsgerXIaJZ',
    yearly: 'price_1Sv9TkECD1n5qAAsm0N8A3eR',
  },
  enthusiast: {
    monthly: 'price_1Sv9CjECD1n5qAAs4hz5mzEw',
    yearly: 'price_1Sv9UyECD1n5qAAsJCkH45p5',
  },
  lover: {
    monthly: 'price_1Sv9DLECD1n5qAAs9TSnuJSA',
    yearly: 'price_1Sv9VzECD1n5qAAsBN13tAA4',
  },
  pro: {
    monthly: 'price_1Sv9DpECD1n5qAAs9E5tJLhY',
    yearly: 'price_1Sv9WeECD1n5qAAsSyT1Ntno',
  },
};

// Cloud Function URL
const CHECKOUT_FUNCTION_URL = 'https://us-central1-polish-all-the-things--app.cloudfunctions.net/createCheckoutSession';

interface PricingTier {
  name: string;
  tierKey: 'free' | 'casual' | 'enthusiast' | 'lover' | 'pro';
  icon: React.ReactNode;
  storage: string;
  monthlyPrice: number;
  yearlyPrice: number;
  description: string;
  features: string[];
  highlighted?: boolean;
  ctaText: string;
}

const tiers: PricingTier[] = [
  {
    name: 'Free',
    tierKey: 'free',
    icon: <Heart className="w-6 h-6" />,
    storage: '50 MB',
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: 'Perfect for getting started',
    features: [
      'Up to ~135 photos',
      'Unlimited items (storage permitting)',
      'Collection management',
      'Wishlist & ISO tracking',
      'Marketplace access',
      'Events calendar',
    ],
    ctaText: 'Get Started',
  },
  {
    name: 'Casual Collector',
    tierKey: 'casual',
    icon: <Star className="w-6 h-6" />,
    storage: '250 MB',
    monthlyPrice: 2.99,
    yearlyPrice: 29.90,
    description: 'For the growing collection',
    features: [
      'Up to ~675 photos',
      'Everything in Free, plus:',
      '5x more storage',
      'Priority support',
    ],
    ctaText: 'Subscribe',
  },
  {
    name: 'Engaged Enthusiast',
    tierKey: 'enthusiast',
    icon: <Gem className="w-6 h-6" />,
    storage: '1 GB',
    monthlyPrice: 4.99,
    yearlyPrice: 49.90,
    description: 'Our most popular plan',
    features: [
      'Up to ~2,700 photos',
      'Everything in Casual, plus:',
      '4x more storage',
      'Perfect for serious collectors',
    ],
    highlighted: true,
    ctaText: 'Subscribe',
  },
  {
    name: 'Lacquer Lover',
    tierKey: 'lover',
    icon: <Crown className="w-6 h-6" />,
    storage: '5 GB',
    monthlyPrice: 9.99,
    yearlyPrice: 99.90,
    description: 'For the dedicated collector',
    features: [
      'Up to ~13,500 photos',
      'Everything in Enthusiast, plus:',
      '5x more storage',
      'Room to grow',
    ],
    ctaText: 'Subscribe',
  },
  {
    name: 'Polish Pro',
    tierKey: 'pro',
    icon: <Sparkles className="w-6 h-6" />,
    storage: '50 GB',
    monthlyPrice: 24.99,
    yearlyPrice: 249.90,
    description: 'For mega-collectors & creators',
    features: [
      'Up to ~135,000 photos',
      'Everything in Lover, plus:',
      '10x more storage',
      'Never worry about space',
    ],
    ctaText: 'Subscribe',
  },
];

export default function PricingPage() {
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Check for canceled checkout
  const wasCanceled = searchParams.get('canceled') === 'true';

  const handleSubscribe = async (tier: PricingTier) => {
    // Free tier just goes to auth/signup
    if (tier.tierKey === 'free') {
      navigate('/auth');
      return;
    }

    // Must be logged in to subscribe
    if (!user) {
      // Save intended tier to redirect back after login
      sessionStorage.setItem('pendingSubscription', JSON.stringify({
        tierKey: tier.tierKey,
        interval: billingInterval,
      }));
      navigate('/auth?redirect=/pricing');
      return;
    }

    setLoadingTier(tier.tierKey);

    try {
      // Get the correct price ID
      const priceId = PRICE_IDS[tier.tierKey as keyof typeof PRICE_IDS][billingInterval];

      const response = await fetch(CHECKOUT_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          userId: user.uid,
          userEmail: user.email,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      // Redirect to Stripe Checkout
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL returned');
      }

    } catch (error) {
      console.error('Checkout error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoadingTier(null);
    }
  };

  // Calculate effective monthly price for yearly billing
  const getEffectiveMonthlyPrice = (yearlyPrice: number) => {
    return (yearlyPrice / 12).toFixed(2);
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <PageHeader
          icon={CreditCard}
          title="Simple, Transparent Pricing"
          subtitle="Start free, upgrade when you need more space. All plans include every feature — you're only paying for storage."
          className="mb-8"
        />

        {/* Canceled Notice */}
        {wasCanceled && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg text-center">
            <p className="text-amber-400 font-body">
              Checkout was canceled. No worries — you can try again whenever you're ready!
            </p>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center items-center gap-4 mb-10">
          <span className={`font-body ${billingInterval === 'monthly' ? 'text-on-bg font-bold' : 'text-on-bg'}`}>
            Monthly
          </span>
          <button
            onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              billingInterval === 'yearly' ? 'bg-slate-600' : 'bg-slate-600'
            }`}
            aria-label="Toggle billing interval"
          >
            <div
              className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                billingInterval === 'yearly' ? 'translate-x-8' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`font-body ${billingInterval === 'yearly' ? 'text-on-bg font-bold' : 'text-on-bg'}`}>
            Yearly
          </span>
          <span className={`bg-blue-500/50 text-black text-xs font-bold px-2 py-1 rounded-full font-body transition-opacity ${
            billingInterval === 'yearly' ? 'opacity-100' : 'opacity-0'
          }`}>
            2 MONTHS FREE
          </span>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-12">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`glass-card p-6 flex flex-col relative ${
                tier.highlighted
                  ? 'ring-2 ring-slate-600 shadow-lg shadow-pink-400/20 overflow-visible'
                  : ''
              }`}
            >
              {/* Popular Badge */}
              {tier.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                  <span className="bg-gray-800 text-white text-xs font-bold px-3 py-1.5 rounded-full font-body whitespace-nowrap">
                    MOST POPULAR
                  </span>
                </div>
              )}

              {/* Icon & Name */}
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-slate-700/50 text-glass">
                  {tier.icon}
                </div>
                <div>
                  <h2 className="font-bold text-glass font-body">{tier.name}</h2>
                  <p className="text-xs text-glass-muted font-body">{tier.storage}</p>
                </div>
              </div>

              {/* Price */}
              <div className="mb-4">
                {tier.monthlyPrice === 0 ? (
                  <div className="text-3xl font-bold text-glass font-heading">Free</div>
                ) : billingInterval === 'monthly' ? (
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-glass font-heading">
                      ${tier.monthlyPrice}
                    </span>
                    <span className="text-glass-muted font-body">/month</span>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-glass font-heading">
                        ${tier.yearlyPrice.toFixed(2)}
                      </span>
                      <span className="text-glass-muted font-body">/year</span>
                    </div>
                    <p className="text-xs text-green-400 font-body mt-1">
                      ${getEffectiveMonthlyPrice(tier.yearlyPrice)}/mo effective
                    </p>
                  </div>
                )}
                <p className="text-sm text-glass-secondary mt-1 font-body">
                  {tier.description}
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-2 mb-6 flex-grow">
                {tier.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm font-body">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-glass-secondary">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              {tier.tierKey === 'free' ? (
                <Link
                  to="/auth"
                  className="w-full py-2 px-4 rounded-lg font-medium text-center transition-default font-body bg-slate-700/50 text-glass hover:bg-slate-600/50"
                >
                  {tier.ctaText}
                </Link>
              ) : (
                <button
                  onClick={() => handleSubscribe(tier)}
                  disabled={loadingTier === tier.tierKey}
                  className={`w-full py-2 px-4 rounded-lg font-medium text-center transition-default font-body flex items-center justify-center gap-2 ${
                    tier.highlighted
                      ? 'btn-primary'
                      : 'bg-slate-700/50 text-glass hover:bg-slate-600/50'
                  } ${loadingTier === tier.tierKey ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {loadingTier === tier.tierKey ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    tier.ctaText
                  )}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Features Highlight */}
        <div className="glass-card p-8 mb-8">
          <h2 className="text-2xl font-bold text-glass mb-6 text-center font-heading">
            Every Plan Includes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Unlimited Items', desc: 'Add as many items as your storage allows' },
              { title: 'All Item Types', desc: 'Polish, plates, tools, acrylics, and more' },
              { title: 'Marketplace Access', desc: 'Buy, sell, and trade with the community' },
              { title: 'Wishlist & ISO', desc: 'Track what you\'re searching for' },
              { title: 'Events Calendar', desc: 'Never miss a sale or release' },
              { title: 'Data Export', desc: 'Your data is always yours to download' },
              { title: 'Privacy Controls', desc: 'You decide what\'s public or private' },
              { title: 'Messaging', desc: 'Connect with other collectors' },
            ].map((feature, index) => (
              <div key={index} className="text-center">
                <h3 className="font-bold text-glass mb-1 font-body">{feature.title}</h3>
                <p className="text-sm text-glass-secondary font-body">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Teaser */}
        <div className="glass-card p-8 text-center">
          <h2 className="text-xl font-bold text-glass mb-4 font-heading">
            Questions About Pricing?
          </h2>
          <p className="text-glass-secondary mb-4 font-body max-w-2xl mx-auto">
            You can cancel or change your plan anytime. If you downgrade, you'll have 30 days 
            to adjust your storage. We never delete your data without warning.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/help"
              className="text-glass hover:text-glass-secondary font-medium font-body hover:underline"
            >
              View FAQ →
            </Link>
            <Link
              to="/refund-policy"
              className="text-glass hover:text-glass-secondary font-medium font-body hover:underline"
            >
              Refund Policy →
            </Link>
            <Link
              to="/contact"
              className="text-glass hover:text-glass-secondary font-medium font-body hover:underline"
            >
              Contact Us →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
